import { useEffect, useRef } from "react";
import SEO from "@/components/SEO";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import { CreditCard, Globe, LinkIcon, CheckCircle2, ArrowRight } from "lucide-react";
import { useTranslation } from "@/i18n/translations";

function AnimatedSection({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }} transition={{ duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] }} className={className}>{children}</motion.div>
  );
}

const SOLUTIONS_DATA = [
  {
    icon: CreditCard,
    titleKey: "features.terminals",
    descKey: "features.terminalsDesc",
    features: ["NFC, Twint, Apple Pay, Google Pay", "4G/5G, WiFi, Ethernet", "PCI-DSS", "Android"],
    image: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL0xqQ2FjZlpkcWNHU01ROGc1aXRtLmpwZyJ9:adyen:Eq072rTMuJ2JEZINaeQi4d2rdAy3BqysmZGFXFnWDSY?format=webp&width=800&height=600",
    link: "/terminaux-de-paiement"
  },
  {
    icon: Globe,
    titleKey: "features.online",
    descKey: "features.onlineDesc",
    features: ["API", "Multi-payment", "Custom checkout", "Real-time analytics"],
    image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxvbmxpbmUlMjBwYXltZW50JTIwY2hlY2tvdXQlMjBlY29tbWVyY2UlMjBsYXB0b3B8ZW58MHx8fHwxNzc4MDIxMzQwfDA&ixlib=rb-4.1.0&q=85",
    link: "/paiement-en-ligne"
  },
  {
    icon: LinkIcon,
    titleKey: "features.links",
    descKey: "features.linksDesc",
    features: ["SMS, Email, WhatsApp", "Real-time tracking", "Custom branding", "One-click pay"],
    image: "https://images.unsplash.com/photo-1586880244543-0528a802be97?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxzbWFydHBob25lJTIwcmVjZWl2aW5nJTIwcGF5bWVudCUyMG5vdGlmaWNhdGlvbiUyMGVtYWlsJTIwU01TfGVufDB8fHx8MTc3ODAyMTQzNnww&ixlib=rb-4.1.0&q=85",
    link: "/liens-de-paiement"
  },
];

const TERMINAL_GRID = [
  { name: "AMS1", tag: "mobile", slug: "ams1", img: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL3Q1Y2VySmpjV1B0UkpEZUR4a21SLnBuZyJ9:adyen:Kxr1RJJmlEr_VW--jzZVrMXBbA9jeCcRU6mQQNcP9KA?format=webp&width=300&height=400" },
  { name: "SFO1", tag: "counter", slug: "sfo1", img: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL2lRTnNmMkpZbXlpRHNZU3p3dmdOLnBuZyJ9:adyen:_IvTntqAuPTeu_ZpRw2fA7TdLa_vOz1SyMVixX0xI_c?format=webp&width=300&height=400" },
  { name: "NYC1", tag: "mobile", slug: "nyc1", img: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL2IzREdwSzRzMmpSV0NYZFJteHo5LnBuZyJ9:adyen:PItds0nupqldFNFSHXLFJvhoGof3pmEmtuxz-1lEsu4?format=webp&width=300&height=400" },
  { name: "S1F2", tag: "mobile", slug: "s1f2", img: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL01hNHR4a3Fib29xbThtZ1VmY3JXLnBuZyJ9:adyen:0fcuuj2UrJ36lqGND2gZAKWNaTUWzI6sHcSO-iawPJU?format=webp&width=300&height=400" },
  { name: "P630", tag: "counter", slug: "p630", img: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL280NGNoMTQ5R2lNbTRlYWJIZFFDLnBuZyJ9:adyen:FZLbWs22rLP6LDkPk9dAV4hu70NZQf9GhDZIpwO22Kc?format=webp&width=300&height=400" },
  { name: "M450", tag: "counter", slug: "m450", img: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcLzN4WlVLNTRTRDNNNThLTHZMRGtMLnBuZyJ9:adyen:zbH86lvJdp8wb4wEN_dwe02ZPFy9S-bxaQ-2UlpPv9w?format=webp&width=300&height=400" },
  { name: "S1E2L", tag: "mobile", slug: "s1e2l", img: "https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJwYXRoIjoiYWR5ZW5cL2ZpbGVcL1djMVFDTTF0U2NHTDZ4Sml3N2o0LnBuZyJ9:adyen:_zMomdQWR15y5m7MQvq7qmiksXeVXx1LNgi74Chotps?format=webp&width=300&height=400" },
];

export default function SolutionsPage() {
  const { t } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="solutions-page">
      <SEO page="solutions" />
      <section className="pt-32 pb-12 lg:pt-36 lg:pb-16 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="max-w-2xl">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("solutions.tag")}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0A1128] tracking-tight mt-4 leading-[1.1]">{t("solutions.title")}</h1>
            <p className="text-base md:text-lg text-[#475569] mt-6 leading-relaxed">{t("solutions.desc")}</p>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection className="text-center mb-16">
            <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#00C853]">{t("solutions.terminalsTag")}</span>
            <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight mt-3">{t("solutions.terminalsTitle")}</h2>
          </AnimatedSection>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-5">
            {TERMINAL_GRID.map((te, i) => (
              <AnimatedSection key={i} delay={i * 0.08}>
                <Link to={`/terminaux/${te.slug}`} className="block">
                  <div className="feature-card bg-[#F8F9FA] rounded-xl border border-slate-200 p-5 text-center">
                    <img src={te.img} alt={te.name} loading="lazy" className="h-40 object-contain mx-auto mb-3" />
                    <span className="text-xs font-medium text-[#00C853]">{t(`terminals.${te.tag}`)}</span>
                    <h3 className="text-base font-semibold text-[#0A1128] mt-1">{te.name}</h3>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {SOLUTIONS_DATA.map((sol, i) => (
        <section key={i} data-testid={`solution-${i}`} className={`py-24 lg:py-32 ${i % 2 === 0 ? "bg-white" : "bg-[#F8F9FA]"}`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center`}>
              <AnimatedSection className={i % 2 !== 0 ? "lg:order-2" : ""}>
                <div className="w-11 h-11 rounded-lg bg-[#1B2B5B]/5 flex items-center justify-center mb-5">
                  <sol.icon size={22} className="text-[#1B2B5B]" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-semibold text-[#0A1128] tracking-tight">{t(sol.titleKey)}</h2>
                <p className="text-base text-[#475569] mt-4 leading-relaxed">{t(sol.descKey)}</p>
                <ul className="mt-6 space-y-3">
                  {sol.features.map((f, fi) => (
                    <li key={fi} className="flex items-start gap-3">
                      <CheckCircle2 size={18} className="text-[#00C853] mt-0.5 shrink-0" />
                      <span className="text-sm text-[#475569]">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={sol.link} className="inline-flex items-center gap-2 mt-8 font-semibold bg-[#00C853] text-white rounded-full px-6 py-2.5 text-sm hover:bg-[#00A844] transition-all">
                  {t("solutions.learnMore")} <ArrowRight size={16} />
                </Link>
              </AnimatedSection>
              <AnimatedSection delay={0.2} className={i % 2 !== 0 ? "lg:order-1" : ""}>
                <div className="rounded-xl overflow-hidden border border-slate-200">
                  <img src={sol.image} alt={t(sol.titleKey)} loading="lazy" className="w-full h-72 lg:h-96 object-cover" />
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>
      ))}

      <section className="py-24 lg:py-32 bg-[#1B2B5B]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">{t("solutions.ctaTitle")}</h2>
            <div className="flex flex-wrap justify-center gap-3 mt-8">
              <Link to="/contact" data-testid="solutions-cta" className="inline-flex items-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-8 py-3.5 text-sm hover:bg-[#00A844] transition-all">
                {t("nav.demo")} <ArrowRight size={16} />
              </Link>
              <Link to="/tarifs" className="inline-flex items-center font-semibold border-2 border-white/20 text-white rounded-full px-8 py-3.5 text-sm hover:bg-white/10 transition-all">
                {t("nav.pricing")}
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
